/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { Box, Button, Stack, Typography, TextField } from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
import { AttachmentResponse, PaylinkResponse } from 'skipify-types';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import dayjs from 'dayjs';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
  ExpirationSchema,
  getPaylinkDescriptionHelperText,
  PAYLINK_DESCRIPTION_MAX_LENGTH,
} from '../../SendPaylinkForm/formValues';
import { DatePicker } from '../DatePicker';
import SkipifyButton from '../../../../components/core/Button/Button';
import { UpdatePaylinkBody } from '../../../../store/modules/paylink/types';
import { CustomerSummary } from '../CustomerSummary';
import Attachment from '../Attachment';
import { usePaylinkDescriptionPasteFeedback } from '../../hooks';

interface Props {
  paylink: PaylinkResponse;
  isLoadingSubmit?: boolean;
  onClose: () => void;
  onSubmit: (data: UpdatePaylinkBody, attachmentsToDelete: string[]) => Promise<void>;
}

const getValidationSchema = ({ expiration }: PaylinkResponse) =>
  z
    .object({
      description: z
        .string()
        .max(PAYLINK_DESCRIPTION_MAX_LENGTH, 'Description must be less than 255 characters')
        .optional(),
      expiration: ExpirationSchema,
    })
    .refine(
      // If the expiration is already set on the paylink, we can't send an empty
      // expiration to the API, so we need to make sure that the expiration is
      // set on the form before submitting
      (data) => !expiration || !!data.expiration,
      {
        message: 'Expiration is required',
        path: ['expiration'],
      },
    );
type FormValues = z.infer<ReturnType<typeof getValidationSchema>>;

export const EditPaylinkForm = ({ paylink, onClose, isLoadingSubmit, onSubmit }: Props) => {
  const { customer, attachments } = paylink;

  const [attachmentsToDelete, setAttachmentsToDelete] = useState<string[]>([]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(getValidationSchema(paylink)),
    defaultValues: {
      description: paylink.description ?? undefined,
      expiration: paylink.expiration ? dayjs(paylink.expiration) : undefined,
    },
    reValidateMode: 'onBlur',
  });

  const descriptionValue = watch('description');
  const { pasteTrimmedMessage, onDescriptionPaste } = usePaylinkDescriptionPasteFeedback();

  const handleDeleteAttachments = (attachmentId: string) => {
    if (!attachmentsToDelete.includes(attachmentId)) {
      setAttachmentsToDelete([...attachmentsToDelete, attachmentId]);
    }
  };

  const handleRemoveFromDeleteAttachments = (attachmentId: string) => {
    const updatedIds = attachmentsToDelete.filter((id) => id !== attachmentId);
    setAttachmentsToDelete(updatedIds);
  };

  const onFormSubmit = async (data: FormValues) => {
    const { description, expiration } = data;
    const requestBody: UpdatePaylinkBody = {};

    const keepingNullDescription = paylink.description == null && description === '';
    if (!keepingNullDescription && description != null && description !== paylink.description) {
      requestBody.description = description;
    }
    const expirationIsoString = expiration?.toISOString();
    if (expirationIsoString && expirationIsoString !== paylink.expiration) {
      requestBody.expiration = expirationIsoString;
    }

    await onSubmit(requestBody, attachmentsToDelete);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        width: '100%',
        height: '100%',
        py: 3,
        px: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <form id="edit-paylink" onSubmit={handleSubmit(onFormSubmit)}>
        <SkipifyButton
          text="Back"
          variant="text"
          onClick={onClose}
          theme="primary"
          startIcon={<ArrowBackIos sx={{ fontSize: '14px !important', color: 'grey.900' }} />}
          sx={{
            marginLeft: -1,
            '& p': {
              fontSize: 12,
              fontWeight: 400,
            },
          }}
        />
        <Typography component="h2" variant="xl" fontWeight="semibold" sx={{ mt: 3, mb: 4, mx: 0 }}>
          Edit Paylink
        </Typography>
        <Box sx={{ mb: 3 }}>
          <CustomerSummary {...customer} />
        </Box>
        <Stack spacing={2}>
          <TextField
            variant="outlined"
            label="Description"
            {...register('description')}
            id="description"
            multiline
            minRows={2}
            inputProps={{
              maxLength: PAYLINK_DESCRIPTION_MAX_LENGTH,
              onPaste: onDescriptionPaste,
            }}
            error={!!errors.description}
            helperText={getPaylinkDescriptionHelperText(
              descriptionValue,
              errors.description?.message,
              pasteTrimmedMessage,
            )}
            FormHelperTextProps={
              pasteTrimmedMessage && !errors.description
                ? { sx: { color: 'text.secondary' } }
                : undefined
            }
          />
          <Controller
            name="expiration"
            control={control}
            render={({ field }) => (
              <DatePicker label="Expiration" errorMessage={errors.expiration?.message} {...field} />
            )}
          />
        </Stack>
        {attachments.length > 0 && (
          <Stack spacing={2} direction="column" marginTop="25px">
            <Typography variant="caption">Attachments</Typography>
            {attachments.map((attachment: AttachmentResponse) => {
              const isAttachmentToDelete = attachmentsToDelete.includes(attachment.attachmentId);

              return (
                <Box key={attachment.attachmentId}>
                  {isAttachmentToDelete ? (
                    <Button
                      variant="text"
                      data-testid="edit-attachment-remove"
                      style={{
                        textTransform: 'none',
                        color: 'grey',
                        fontWeight: 500,
                        lineHeight: 1.75,
                        backgroundColor: 'white',
                        textDecoration: 'line-through',
                      }}
                      onClick={() => handleRemoveFromDeleteAttachments(attachment.attachmentId)}
                      endIcon={<AddCircleIcon />}
                    >
                      {attachment.displayName}
                    </Button>
                  ) : (
                    <Attachment
                      attachment={attachment}
                      handleClick={() => handleDeleteAttachments(attachment.attachmentId)}
                      endIcon={<RemoveCircleIcon />}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </form>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          paddingTop: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
        }}
      >
        <SkipifyButton
          text="Cancel"
          onClick={onClose}
          theme="secondary"
          isFullWidth
          form="edit-paylink"
        />
        <SkipifyButton
          text="Save"
          isFullWidth
          type="submit"
          form="edit-paylink"
          disabled={isLoadingSubmit}
          loading={isLoadingSubmit}
        />
      </Box>
    </Box>
  );
};
